import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAnalyses } from "@/context/AnalysisContext";
import { AdAnalysisDetail } from "@/components/AdAnalysisDetail";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AnalysisView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { analyses, remove } = useAnalyses();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const local = analyses.find((a) => a.id === id);
    if (local) {
      setAnalysis(local);
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        if (active) setAnalysis(data);
      } catch {
        if (active) toast.error("Analysis not found.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, analyses]);

  const doDelete = async () => {
    try {
      await remove(id);
      toast.success("Analysis deleted");
      navigate("/ad-library");
    } catch {
      toast.error("Could not delete analysis.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-24">
        <p className="text-muted-foreground">This analysis could not be found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/ad-library")}>
          Back to Ad Library
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button variant="ghost" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={doDelete} data-testid="delete-analysis-btn">
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </div>
      <AdAnalysisDetail analysis={analysis} />
    </div>
  );
}
